import config from '/public/config'
import store from '@/store/store'

// WebSocket만 직결 (port 443, Nginx 프록시) — 그 외 모든 HTTP API는
// store dispatch → HttpRequest 경유 → OctoberCMS(port 8080) → FastAPI
const WS_URL        = config.BASE_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/voice/ws/live'
const WORKLET_URL   = '/pcm-worklet.js'
const TARGET_SR     = 16000
const DRAIN_TIMEOUT = 2000

// 비용 보호 — 최대 세션 시간 / 무음 자동 종료
const MAX_SESSION_MS    = 15 * 60 * 1000  // 15분
const IDLE_TIMEOUT_MS   = 60 * 1000       // 60초
const SESSION_WARN_1_MS = 2 * 60 * 1000   // 종료 2분 전 경고
const SESSION_WARN_2_MS = 30 * 1000       // 종료 30초 전 경고
const IDLE_WARN_MS      = 10 * 1000       // 무음 종료 10초 전 경고

export function useVoiceRealtimeTranslate() {
  const state = {
    isRecording:        false,
    isConnecting:       false,
    liveReady:          false,    // Gemini Live setup 완료 여부
    sttText:            '',       // accumulated_stt — 확정된 누적 원문
    translatedText:     '',       // accumulated_translation — 확정된 누적 번역
    previewText:        '',       // 진행 중 partial 원문
    previewTranslation: '',       // 진행 중 partial 번역
    turns:              [],       // VAD 단위 발화 카드 목록 [{id, time, original, translated, sourceLang, targetLang}]
    autoStopReason:     null,     // 'max_session' | 'idle' | null
    error:              null,
    sessionId:          null,
    recordingId:        null,     // WS auth_ok에서 받는 녹음 UUID (히스토리 단축 진입용)
  }

  let ws               = null
  let stream           = null
  let audioCtx         = null
  let workletNode      = null
  let sourceNode       = null
  let currentSource    = 'ko'
  let currentTarget    = 'en'
  let onStateChange    = null
  let onEvent          = null    // 일회성 이벤트 콜백 (경고/자동종료)
  let intentionalClose = false
  let isPaused         = false   // true면 worklet 청크를 송신하지 않음
  let pipelineActive   = false

  // 비용 보호 타이머
  let sessionWarn1Timer = null
  let sessionWarn2Timer = null
  let sessionMaxTimer   = null
  let idleWarnTimer     = null
  let idleStopTimer     = null
  let autoStopping      = false

  function _notify() {
    if (onStateChange) {
      onStateChange(Object.assign({}, state))
    }
  }

  function setOnStateChange(fn) {
    onStateChange = fn
  }

  function setOnEvent(fn) {
    onEvent = fn
  }

  function _emit(evt) {
    if (onEvent) {
      try { onEvent(evt) } catch (e) { /* ignore */ }
    }
  }

  // ── 세션 시간 / 무음 타이머 ──────────────────────────────────
  function _startSessionTimers() {
    _clearSessionTimers()
    sessionWarn1Timer = setTimeout(function() {
      _emit({ type: 'session_warning', remainingMs: SESSION_WARN_1_MS })
    }, MAX_SESSION_MS - SESSION_WARN_1_MS)
    sessionWarn2Timer = setTimeout(function() {
      _emit({ type: 'session_warning', remainingMs: SESSION_WARN_2_MS })
    }, MAX_SESSION_MS - SESSION_WARN_2_MS)
    sessionMaxTimer = setTimeout(function() {
      _autoStop('max_session')
    }, MAX_SESSION_MS)
  }

  function _resetIdleTimer() {
    if (idleWarnTimer) { clearTimeout(idleWarnTimer); idleWarnTimer = null }
    if (idleStopTimer) { clearTimeout(idleStopTimer); idleStopTimer = null }
    idleWarnTimer = setTimeout(function() {
      _emit({ type: 'idle_warning', remainingMs: IDLE_WARN_MS })
    }, IDLE_TIMEOUT_MS - IDLE_WARN_MS)
    idleStopTimer = setTimeout(function() {
      _autoStop('idle')
    }, IDLE_TIMEOUT_MS)
  }

  function _clearSessionTimers() {
    if (sessionWarn1Timer) { clearTimeout(sessionWarn1Timer); sessionWarn1Timer = null }
    if (sessionWarn2Timer) { clearTimeout(sessionWarn2Timer); sessionWarn2Timer = null }
    if (sessionMaxTimer)   { clearTimeout(sessionMaxTimer);   sessionMaxTimer   = null }
  }

  function _clearAllTimers() {
    _clearSessionTimers()
    if (idleWarnTimer) { clearTimeout(idleWarnTimer); idleWarnTimer = null }
    if (idleStopTimer) { clearTimeout(idleStopTimer); idleStopTimer = null }
  }

  async function _autoStop(reason) {
    if (autoStopping) return
    autoStopping = true
    state.autoStopReason = reason
    _notify()
    var summary = await stop()
    autoStopping = false
    _emit({ type: 'auto_stop', reason: reason, summary: summary })
  }

  // ── 세션 시작 ──────────────────────────────────────────────────
  async function start(options) {
    if (!options) {
      options = {}
    }
    currentSource      = options.sourceLang ? options.sourceLang : 'ko'
    currentTarget      = options.targetLang ? options.targetLang : 'en'
    // 이전 세션의 잔여 상태 리셋 — 특히 stop()이 설정한 isPaused=true가
    // 다음 녹음의 worklet 청크 송신을 막는 것을 방지
    isPaused           = false
    intentionalClose   = false
    pipelineActive     = false
    state.error        = null
    state.isConnecting = true
    state.liveReady    = false
    state.previewText        = ''
    state.previewTranslation = ''
    state.turns              = []
    state.autoStopReason     = null
    state.recordingId        = null
    _notify()

    try {
      console.log('[VoiceTranslate] 토큰 발급 + getUserMedia 병렬 시작')
      // OctoberCMS 경유로 토큰 발급 (user_id/username은 JWT에서 추출되므로 보내지 않음)
      var results = await Promise.all([
        store.dispatch('miras/VOICE_TOKEN', {
          source_language: currentSource,
          target_language: currentTarget,
        }),
        navigator.mediaDevices.getUserMedia({ audio: true }),
      ])

      var tokenRes = results[0]
      stream       = results[1]
      console.log('[VoiceTranslate] 토큰 발급 + getUserMedia 완료')

      // HttpRequest는 axios response의 data를 그대로 반환하지 않을 수 있음 — 양쪽 형식 모두 지원
      var tokenData = tokenRes && tokenRes.data ? tokenRes.data : tokenRes
      state.sessionId = tokenData.session_id
      var wsToken = tokenData.ws_token ? tokenData.ws_token : tokenData.token

      if (state.sessionId) {
        sessionStorage.setItem('voice_session_id', state.sessionId)
      }

      await _connectWebSocket(wsToken)

    } catch (e) {
      _cleanupAudioPipeline()
      if (stream) {
        stream.getTracks().forEach(function(t) { t.stop() })
        stream = null
      }
      if (e && e.response && e.response.data && e.response.data.error) {
        state.error = e.response.data.error
      } else if (e && e.name === 'NotFoundError') {
        state.error = '마이크 장치를 찾을 수 없습니다. 마이크가 연결되어 있는지 확인해 주세요.'
      } else if (e && e.message) {
        state.error = e.message
      } else {
        state.error = '연결 실패'
      }
      state.isConnecting = false
      _notify()
    }
  }

  // ── WebSocket 연결 ─────────────────────────────────────────────
  function _connectWebSocket(wsToken) {
    return new Promise(function(resolve, reject) {
      console.log('[VoiceTranslate] WS 연결 시도:', WS_URL)
      ws = new WebSocket(WS_URL)

      ws.onopen = function() {
        var savedSessionId = sessionStorage.getItem('voice_session_id')
        ws.send(JSON.stringify({
          type:       'auth',
          token:      wsToken,
          session_id: savedSessionId ? savedSessionId : null,
        }))
      }

      ws.onmessage = function(event) {
        var msg
        try {
          msg = JSON.parse(event.data)
        } catch (e) {
          return
        }

        if (msg.type === 'auth_ok') {
          // 주의: auth_ok의 session_id는 FastAPI-side ID이고,
          // OctoberCMS HTTP API(/voice/history, /voice/session/{id}/end)는
          // /voice/token에서 받은 OctoberCMS-side ID를 사용해야 함.
          // 따라서 state.sessionId(/voice/token으로 이미 세팅됨)를 덮어쓰지 않음.
          // 단, /voice/token 응답이 없는 예외적 재연결 시나리오만 폴백 처리.
          if (msg.session_id && !state.sessionId) {
            state.sessionId = msg.session_id
            sessionStorage.setItem('voice_session_id', msg.session_id)
          }
          if (msg.recording_id) {
            state.recordingId = msg.recording_id
          }
          if (msg.accumulated_stt) {
            state.sttText = msg.accumulated_stt
          }
          if (msg.accumulated_translation) {
            state.translatedText = msg.accumulated_translation
          }
          state.isConnecting = false
          state.isRecording  = true
          _notify()
          // 비용 보호 타이머 가동
          _startSessionTimers()
          _resetIdleTimer()
          // auth_ok 즉시 오디오 파이프라인 시작 — 첫 청크가 200ms 안에 도달
          _startAudioPipeline().then(resolve).catch(function(err) {
            state.error = 'AudioWorklet 시작 실패: ' + err.message
            _notify()
            reject(err)
          })
          return
        }

        if (msg.type === 'auth_error') {
          state.error        = msg.message
          state.isConnecting = false
          _notify()
          reject(new Error(msg.message))
          return
        }

        if (msg.type === 'ready') {
          state.liveReady = true
          _notify()
          return
        }

        if (msg.type === 'partial') {
          state.previewText        = msg.original   ? msg.original   : ''
          state.previewTranslation = msg.translated ? msg.translated : ''
          _resetIdleTimer()
          _notify()
          return
        }

        if (msg.type === 'turn_complete') {
          // 서버가 보내준 누적으로 교체 (단순 append 아님)
          if (msg.accumulated_stt) {
            state.sttText = msg.accumulated_stt
          } else if (msg.original) {
            state.sttText = (state.sttText + ' ' + msg.original).trim()
          }
          if (msg.accumulated_translation) {
            state.translatedText = msg.accumulated_translation
          } else if (msg.translated) {
            state.translatedText = (state.translatedText + ' ' + msg.translated).trim()
          }
          // VAD 단위 카드 추가 — 빈 발화 / 구두점만 있는 발화는 무시
          // (녹음 종료 직전 짧은 VAD가 "." 같은 의미 없는 결과를 만들 수 있음)
          var turnOriginal   = msg.original   ? msg.original   : ''
          var turnTranslated = msg.translated ? msg.translated : ''
          if (_isMeaningful(turnOriginal) || _isMeaningful(turnTranslated)) {
            var now = new Date()
            state.turns = state.turns.concat([{
              id:          Date.now() + '_' + Math.random().toString(36).slice(2, 8),
              time:        _fmtTime(now),
              // 한쪽만 의미 있는 경우 다른 쪽은 비워둠 — "." 같은 노이즈 표시 방지
              original:    _isMeaningful(turnOriginal)   ? turnOriginal   : '',
              translated:  _isMeaningful(turnTranslated) ? turnTranslated : '',
              sourceLang:  msg.source_language ? msg.source_language : currentSource,
              targetLang:  msg.target_language ? msg.target_language : currentTarget,
            }])
          }
          state.previewText        = ''
          state.previewTranslation = ''
          _resetIdleTimer()
          _notify()

          _saveMessage(msg).catch(function(e) {
            console.warn('[VoiceHistory] 저장 실패:', e.message)
          })
          return
        }

        if (msg.type === 'interrupted') {
          // 화자 중단 — partial 비움
          state.previewText        = ''
          state.previewTranslation = ''
          _notify()
          return
        }

        if (msg.type === 'error') {
          state.error = msg.message
          _notify()
          return
        }

        if (msg.type === 'ping') {
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'pong' }))
          }
          return
        }

        console.log('[VoiceTranslate] 알 수 없는 메시지 type:', msg.type)
      }

      ws.onerror = function(e) {
        console.warn('[VoiceTranslate] ws.onerror', e)
        state.error        = 'WebSocket 연결 오류'
        state.isConnecting = false
        state.isRecording  = false
        state.liveReady    = false
        _notify()
        reject(new Error('WebSocket 연결 오류'))
      }

      ws.onclose = function(e) {
        console.warn('[VoiceTranslate] ws.onclose — intentional:', intentionalClose, 'code:', e.code, 'reason:', e.reason)
        intentionalClose   = false
        state.isRecording  = false
        state.isConnecting = false
        state.liveReady    = false
        _notify()
        _clearAllTimers()
        _cleanupAudioPipeline()
      }
    })
  }

  // ── 오디오 파이프라인 시작 (AudioWorklet) ─────────────────────
  async function _startAudioPipeline() {
    var Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) {
      throw new Error('AudioContext 미지원 브라우저')
    }

    // sampleRate: 16000 옵션 — 브라우저가 지원하면 채택, 아니면 디바이스 기본값으로 생성됨
    try {
      audioCtx = new Ctx({ sampleRate: TARGET_SR })
    } catch (e) {
      audioCtx = new Ctx()
    }

    // iOS Safari: suspended 상태일 수 있으므로 resume
    if (audioCtx.state === 'suspended') {
      try { await audioCtx.resume() } catch (e) { /* ignore */ }
    }

    await audioCtx.audioWorklet.addModule(WORKLET_URL)

    sourceNode  = audioCtx.createMediaStreamSource(stream)
    workletNode = new AudioWorkletNode(audioCtx, 'pcm-worklet', {
      processorOptions: { inputSampleRate: audioCtx.sampleRate },
    })

    workletNode.port.onmessage = function(e) {
      if (isPaused) return
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      try {
        var base64 = _bufToBase64(e.data)
        ws.send(JSON.stringify({ type: 'audio_chunk', data: base64 }))
      } catch (err) {
        console.warn('[VoiceTranslate] 청크 전송 실패:', err)
      }
    }

    sourceNode.connect(workletNode)
    // workletNode는 destination에 연결하지 않음 — 출력 불필요
    pipelineActive = true

    console.log('[VoiceTranslate] AudioWorklet 시작 — sampleRate:', audioCtx.sampleRate)
  }

  // ── 오디오 파이프라인 정리 ────────────────────────────────────
  function _cleanupAudioPipeline() {
    pipelineActive = false
    if (workletNode) {
      try { workletNode.port.onmessage = null } catch (e) { /* ignore */ }
      try { workletNode.disconnect() } catch (e) { /* ignore */ }
      workletNode = null
    }
    if (sourceNode) {
      try { sourceNode.disconnect() } catch (e) { /* ignore */ }
      sourceNode = null
    }
    if (audioCtx) {
      try { audioCtx.close() } catch (e) { /* ignore */ }
      audioCtx = null
    }
  }

  // ── 시간 포맷 (HH:MM:SS) ──────────────────────────────────────
  function _fmtTime(d) {
    function pad(n) { return n < 10 ? '0' + n : '' + n }
    return pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds())
  }

  // ── 의미 있는 텍스트인지 검사 ────────────────────────────────
  // 공백·구두점·기호만 있으면 false (빈 카드 / "." 카드 방지)
  function _isMeaningful(s) {
    if (!s) return false
    // 한글·한자·일본어·라틴·키릴·아랍 등 문자/숫자만 남기고 제거
    // \p{L}(letter), \p{N}(number) 지원 환경이면 더 정확하지만, ES5 호환 위해 명시 패턴 사용
    var cleaned = String(s).replace(/[\s.,!?;:'"`~@#$%^&*()\[\]{}<>\/\\|+=\-_…。、，！？；：「」『』（）·・]/g, '')
    return cleaned.length > 0
  }

  // ── ArrayBuffer → base64 (스택 오버플로 방지) ─────────────────
  function _bufToBase64(buf) {
    var bytes = new Uint8Array(buf)
    var binary = ''
    var chunkSize = 8192
    for (var i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize))
    }
    return btoa(binary)
  }

  // ── 히스토리 저장 ─────────────────────────────────────────────
  async function _saveMessage(msg) {
    if (!state.sessionId) return
    await store.dispatch('miras/VOICE_HISTORY', {
      session_id:        state.sessionId,
      chunk_index:       msg.chunk_index ? msg.chunk_index : 0,
      stt_text:          msg.original   ? msg.original   : msg.transcribed_text,
      translated_text:   msg.translated ? msg.translated : msg.translated_text,
      detected_language: msg.detected_language,
      source_language:   msg.source_language   ? msg.source_language   : currentSource,
      target_language:   msg.target_language   ? msg.target_language   : currentTarget,
      processing_time:   msg.processing_time,
      stt_time:          msg.stt_time,
      translation_time:  msg.translation_time,
    })
  }

  // ── 일시정지 / 재개 (송신 차단만) ─────────────────────────────
  function pause() {
    isPaused = true
    console.log('[VoiceTranslate] 일시정지 (송신 차단)')
  }

  function resume() {
    isPaused = false
    console.log('[VoiceTranslate] 재개')
  }

  // ── 세션 종료 ─────────────────────────────────────────────────
  async function stop() {
    // ① 오디오 파이프라인 즉시 중단
    isPaused = true
    _clearAllTimers()
    _cleanupAudioPipeline()

    if (stream) {
      stream.getTracks().forEach(function(t) { t.stop() })
      stream = null
    }

    state.isRecording = false
    state.liveReady   = false
    _notify()

    // ② 서버에 end_of_stream / stop 신호 전송
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: 'end_of_stream' })) } catch (e) { /* ignore */ }
      try { ws.send(JSON.stringify({ type: 'stop' })) } catch (e) { /* ignore */ }
    }

    // ③ 마지막 turn_complete 수신 대기 (최대 DRAIN_TIMEOUT)
    await new Promise(function(resolve) {
      if (!ws || ws.readyState === WebSocket.CLOSED) {
        resolve()
        return
      }
      var done = false
      var timer = setTimeout(function() {
        if (!done) { done = true; resolve() }
      }, DRAIN_TIMEOUT)
      var prevOnClose = ws.onclose
      ws.onclose = function(e) {
        if (!done) { done = true; clearTimeout(timer); resolve() }
        if (prevOnClose) { prevOnClose(e) }
      }
    })

    // ④ WS 정리
    intentionalClose = true
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.close()
    }
    ws = null
    state.isConnecting = false
    _notify()

    // ⑤ 세션 종료 API
    if (state.sessionId) {
      try {
        var sessionId = state.sessionId
        sessionStorage.removeItem('voice_session_id')
        var res = await store.dispatch('miras/VOICE_SESSION_END', { sessionId: sessionId })
        return res && res.data ? res.data : res
      } catch (e) {
        console.warn('[VoiceHistory] 세션 종료 실패:', e.message)
      }
    }
    return null
  }

  // ── 텍스트 초기화 ─────────────────────────────────────────────
  function resetTexts() {
    state.sttText            = ''
    state.translatedText     = ''
    state.previewText        = ''
    state.previewTranslation = ''
    state.turns              = []
    state.autoStopReason     = null
    state.recordingId        = null
    state.sessionId          = null
    state.error              = null
    state.liveReady          = false
    sessionStorage.removeItem('voice_session_id')
    _notify()
  }

  // ── 세션 히스토리 API ─────────────────────────────────────────
  async function fetchSessions(params) {
    if (!params) {
      params = {}
    }
    var res = await store.dispatch('miras/VOICE_SESSIONS_LIST', params)
    return res && res.data ? res.data : res
  }

  async function fetchSessionDetail(sessionId) {
    var res = await store.dispatch('miras/VOICE_SESSION_DETAIL', { sessionId: sessionId })
    return res && res.data ? res.data : res
  }

  async function deleteSession(sessionId) {
    await store.dispatch('miras/VOICE_SESSION_DELETE', { sessionId: sessionId })
  }

  return {
    state,
    setOnStateChange,
    setOnEvent,
    start,
    pause,
    resume,
    stop,
    resetTexts,
    fetchSessions,
    fetchSessionDetail,
    deleteSession,
  }
}
